(function () {
  const MAX_BATCH = 50;
  const OBSERVER_DEBOUNCE_MS = 120;

  const CLEANUP_KEY = "__dapPlpCleanup";

  function pickInventoryItem(inventory, variantId) {
    if (!inventory || variantId == null || variantId === "") return undefined;
    const n = String(variantId).trim();
    if (inventory[n]) return inventory[n];
    const gid = n.startsWith("gid://")
      ? n
      : `gid://shopify/ProductVariant/${n}`;
    if (inventory[gid]) return inventory[gid];
    const suffix = gid.split("/").pop();
    if (suffix && inventory[suffix]) return inventory[suffix];
    return undefined;
  }

  function initRoot(root) {
    const gatingEnabled = root.dataset.inventoryGatingEnabled === "true";
    if (!gatingEnabled) return null;

    const banner = root.querySelector("[data-dap-plp-banner]");
    const emptyMessage = root.querySelector("[data-dap-plp-empty]");
    const t = (key) =>
      window.__DAP_I18N?.[key] ?? "Choose date and address";

    let fetchController = null;
    let debounceTimer = null;
    let observer = null;

    function updateEmptyState() {
      if (!emptyMessage) return;
      const resolved = root.hasAttribute("data-dap-resolved");
      if (!resolved) {
        emptyMessage.hidden = true;
        return;
      }
      const cards = [...root.querySelectorAll("[data-dap-product-card]")];
      const visibleCards = cards.filter(
        (c) => !c.classList.contains("is-unavailable-for-date"),
      );
      emptyMessage.hidden = visibleCards.length > 0 || cards.length === 0;
    }

    function setSectionResolved(resolved) {
      for (const section of document.querySelectorAll("[data-dap-section]")) {
        if (resolved) {
          section.setAttribute("data-dap-resolved", "");
        } else {
          section.removeAttribute("data-dap-resolved");
        }
      }
      if (resolved) {
        root.setAttribute("data-dap-resolved", "");
      } else {
        root.removeAttribute("data-dap-resolved");
      }
    }

    function shouldSkipCard(card) {
      return (
        card.getAttribute("data-requires-shipping") === "false" ||
        card.getAttribute("data-inventory-management") === "" ||
        card.getAttribute("data-inventory-policy") === "continue"
      );
    }

    function abortPending() {
      fetchController?.abort();
      fetchController = null;
    }

    function findCarouselSlide(card) {
      const slide = card.closest("slideshow-slide");
      if (!slide) return null;
      return root.contains(slide) ? slide : null;
    }

    function resetCards() {
      for (const card of root.querySelectorAll("[data-dap-product-card]")) {
        card.classList.remove("is-unavailable-for-date");
        const badge = card.querySelector("[data-dap-plp-badge]");
        if (badge) badge.hidden = true;
        const slide = findCarouselSlide(card);
        if (slide) slide.removeAttribute("hidden");
      }
      updateEmptyState();
    }

    async function checkAll() {
      const currentSession = window.DAP.getSession();
      if (!currentSession || !window.DAP.isSessionValid()) {
        abortPending();
        if (banner) {
          banner.hidden = false;
          banner.textContent = t("plp_choose_date");
        }
        setSectionResolved(false);
        resetCards();
        return;
      }

      const locationId = currentSession.location_id ?? currentSession.location;
      if (!locationId) {
        abortPending();
        if (banner) {
          banner.hidden = false;
          banner.textContent = t("plp_choose_date");
        }
        setSectionResolved(false);
        resetCards();
        return;
      }

      if (banner) banner.hidden = true;
      setSectionResolved(true);

      const cards = [...root.querySelectorAll("[data-dap-product-card]")];
      const toCheck = cards.filter((c) => !shouldSkipCard(c));

      if (toCheck.length === 0) return;

      fetchController?.abort();
      fetchController = new AbortController();
      const { signal } = fetchController;

      const variantIds = toCheck.map((c) =>
        c.getAttribute("data-variant-id"),
      );

      const batches = [];
      for (let i = 0; i < variantIds.length; i += MAX_BATCH) {
        batches.push(variantIds.slice(i, i + MAX_BATCH));
      }

      const inventoryMap = {};

      for (const batch of batches) {
        try {
          const resp = await fetch(
            `/apps/delivery/inventory?location_id=${encodeURIComponent(locationId)}&variant_ids=${encodeURIComponent(batch.join(","))}`,
            { signal },
          );
          if (!resp.ok) continue;
          const { inventory } = await resp.json();
          Object.assign(inventoryMap, inventory);
        } catch (err) {
          if (err?.name === "AbortError") return;
        }
      }

      for (const card of toCheck) {
        const vid = card.getAttribute("data-variant-id");
        const item = pickInventoryItem(inventoryMap, vid);
        const badge = card.querySelector("[data-dap-plp-badge]");
        const slide = findCarouselSlide(card);

        if (item && item.tracked && item.qty <= 0) {
          card.classList.add("is-unavailable-for-date");
          if (badge) badge.hidden = false;
          if (slide) slide.setAttribute("hidden", "");
        } else {
          card.classList.remove("is-unavailable-for-date");
          if (badge) badge.hidden = true;
          if (slide) slide.removeAttribute("hidden");
        }
      }

      updateEmptyState();
    }

    function scheduleCheckAll() {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        checkAll();
      }, OBSERVER_DEBOUNCE_MS);
    }

    if (typeof MutationObserver !== "undefined") {
      observer = new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (m.type !== "childList") continue;
          for (const node of m.addedNodes) {
            if (node.nodeType !== 1) continue;
            if (
              node.hasAttribute("data-dap-product-card") ||
              node.querySelector?.("[data-dap-product-card]")
            ) {
              scheduleCheckAll();
              return;
            }
          }
        }
      });
      observer.observe(root, { childList: true, subtree: true });
    }

    const unsub = window.DAP.onSessionChange(() => checkAll());

    checkAll();

    return function cleanup() {
      if (unsub) unsub();
      abortPending();
      if (debounceTimer) clearTimeout(debounceTimer);
      if (observer) observer.disconnect();
    };
  }

  function init() {
    const prev = globalThis[CLEANUP_KEY];
    if (typeof prev === "function") prev();

    if (!window.DAP) return;

    const roots = document.querySelectorAll("[data-dap-plp]");
    if (roots.length === 0) return;

    const rootCleanups = [];

    for (const root of roots) {
      const rootCleanup = initRoot(root);
      if (rootCleanup) rootCleanups.push(rootCleanup);
    }

    if (rootCleanups.length === 0) return;

    function cleanup() {
      for (const fn of rootCleanups) fn();
      rootCleanups.length = 0;
      if (globalThis[CLEANUP_KEY] === cleanup) {
        delete globalThis[CLEANUP_KEY];
      }
    }

    globalThis[CLEANUP_KEY] = cleanup;

    return cleanup;
  }

  init();
})();
